import axios, { AxiosError, AxiosInstance } from "axios";
import { useMutation, useQuery } from "react-query";
import {
  ChatQueryRequest,
  ChatSuggestionsRequest,
} from "../request/ChatRequest";
import {
  ChatQueryResponse,
  ChatSuggestionsResponse,
} from "../model/ChatModels";
import { JSONRequestHeaders } from "../../../../providers/HttpClient";

// Create a custom axios instance for the AI Insights service
const createAIInsightsClient = (): AxiosInstance => {
  const aiServiceUrl =
    process.env.REACT_APP_AI_SERVICE_URL || "http://localhost:8001";

  return axios.create({
    baseURL: aiServiceUrl,
    headers: {
      ...JSONRequestHeaders,
    },
  });
};

const aiInsightsClient = createAIInsightsClient();

/**
 * Service to send a chat query to the AI Insights backend
 */
const ChatQueryApiService = async (
  request: ChatQueryRequest
): Promise<ChatQueryResponse> => {
  const url = "/api/chat/query";
  const { data } = await aiInsightsClient.post(url, request);
  return data;
};

/**
 * React Query hook for sending chat queries
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useChatQueryApiService = () =>
  useMutation<ChatQueryResponse, AxiosError, ChatQueryRequest>(
    ChatQueryApiService,
    {
      mutationKey: "ChatQueryApiService",
    }
  );

/**
 * Service to get suggested queries by category
 */
const ChatSuggestionsApiService = async (
  request: ChatSuggestionsRequest
): Promise<ChatSuggestionsResponse> => {
  const url = "/api/chat/suggestions";
  const { data } = await aiInsightsClient.get(url, { params: request });
  return data;
};

/**
 * React Query hook for getting chat suggestions
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useChatSuggestionsApiService = (request: ChatSuggestionsRequest) =>
  useQuery<ChatSuggestionsResponse, AxiosError>(
    ["ChatSuggestionsApiService", request.category],
    () => ChatSuggestionsApiService(request),
    {
      enabled: !!request.category,
      keepPreviousData: true,
    }
  );
